import {customElement, property} from 'lit/decorators.js';
import {css, CSSResult, html, LitElement, PropertyValues, TemplateResult, unsafeCSS,} from 'lit';
import {consume} from '@lit/context';
import {ProductIdentifierContainer, Messages, OpenSizeLayerContainer} from '../../../@types/wishlist.js';
import typographyStyles from '../../../styles/responsive-nui-typography.scss';
import {i18nContext} from '../../../scripts/context/i18nContext.js';

interface Add2CartPayload {
  artikel: string;
  vertriebsinfoId: string;
  menge: number;
}

@customElement('ents-add-to-cart-button')
export class AddToCartButton extends LitElement {
  @property({attribute: 'identifier', type: String, reflect: true})
  private identifier: string = '';

  @property({attribute: 'article-id', type: String, reflect: true})
  private articleId: string = '';

  @property({attribute: 'sales-information-id', type: String, reflect: true})
  private salesInformationId: string = '';

  @property({type: String})
  private additionalInfoForAriaLabel: string = '';

  @property({type: Boolean, reflect: true})
  private trigger: boolean = false;

  @consume({context: i18nContext})
  private messages!: Messages;

  static get styles(): CSSResult {
    return css`${unsafeCSS(typographyStyles)}`;
  }

  private getPayload(): Add2CartPayload {
    return {
      artikel: this.articleId,
      vertriebsinfoId: this.salesInformationId,
      menge: 1,
    };
  }

  protected updated(_changedProperties: PropertyValues) {
    super.updated(_changedProperties);

    if (_changedProperties.has('trigger') && this.trigger) {
      // wait until layer is closed with animation, see bug ENTS-8514
      setTimeout(() => {
        this.emitEvent();
      }, 200);
      this.trigger = false;
    }
  }

  private emitEvent() {
    if (this.articleId && this.salesInformationId) {
      const payload = this.getPayload();
      document.dispatchEvent(new CustomEvent('kaufen:cart:add', {detail: payload}));
      this.dispatchEvent(new CustomEvent<ProductIdentifierContainer>('ents:cta-event-emitted', {
        detail: {
          identifier: this.identifier,
        },
        bubbles: true,
        composed: true,
      }));
      this.setAttribute('data-test-ents-add-to-cart-button-payload-emitted', JSON.stringify(payload));
    } else {
      this.dispatchEvent(new CustomEvent<OpenSizeLayerContainer>('ents:size-layer:open', {
        detail: {
          addToCartAfterSizeSelection: true,
          tracking: {
            context: 'wishlist_cart_size_menu',
          },
        },
        bubbles: true,
        composed: true,
      }));
    }
  }

  protected render(): TemplateResult {
    return html`
      <div class="ents-addtocart-button" @click="${this.emitEvent}" data-test-ents-add-to-cart-button-shadow>
        <button
          class="bdk-button"
          variant="highlight"
          size="medium"
          full-width
          aria-label="${this.messages['ents.wishlist.product.cta.addToCart']} ${this.additionalInfoForAriaLabel}"
          type="button">
          <span
            class="ents-addtocart-button__button-text">${this.messages['ents.wishlist.product.cta.addToCart']}
            </span>
        </button>
      </div>`;
  }
}
